/**
 * Recording taps a MediaStreamAudioDestinationNode off the master bus — never
 * an offline re-render, so what you export is exactly what you heard, timing
 * artifacts included (architecture.md invariant 6).
 *
 * The codec is not portable, and asking for a bare container is a trap:
 *
 *   MediaRecorder.isTypeSupported('audio/mp4') is true in Chromium, but the
 *   file it then writes is an MP4 containing OPUS (stsd -> 'Opus'/'dOps').
 *   Apple's AVFoundation plays MP4/AAC and not MP4/Opus, so that take opens on
 *   iOS as valid media that cannot be decoded — a crossed-out play button.
 *   Safari, which has no WebM encoder, resolves the same bare 'audio/mp4' to
 *   real AAC and is fine.
 *
 * So: ask for AAC explicitly first; if that is unavailable, prefer a correctly
 * labelled WebM over a bare MP4 that may silently be Opus; and only fall back
 * to bare MP4 on engines with no WebM encoder at all, which is the Safari case
 * where bare MP4 does mean AAC.
 */

/** Explicit AAC-in-MP4. Where this is offered, it is the most portable take. */
const AAC_MP4 = ['audio/mp4;codecs=mp4a.40.2', 'audio/mp4;codecs=mp4a.40.5'];
/** Honest, correctly-labelled Opus containers. */
const OPUS = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm'];
/** Last resort: bare MP4. Only reached when the engine cannot do WebM at all. */
const BARE_MP4 = ['audio/mp4'];

type SupportFn = (t: string) => boolean;

const defaultSupport: SupportFn = (t) =>
  typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t);

/**
 * Pick a recording mime type, preferring containers whose codec we can predict.
 * Exported for test: the ordering rule is the whole fix, so it is asserted
 * directly rather than through a live MediaRecorder.
 */
export function pickMimeType(supported: SupportFn = defaultSupport): string | null {
  for (const t of AAC_MP4) if (supported(t)) return t;
  for (const t of OPUS) if (supported(t)) return t;
  for (const t of BARE_MP4) if (supported(t)) return t;
  return null;
}

/** Strip the `;codecs=…` parameter and normalize case. */
export const containerOf = (mime: string): string => mime.split(';')[0].trim().toLowerCase();

const codecOf = (mime: string): string => {
  const m = /codecs\s*=\s*"?([^";]+)"?/i.exec(mime);
  return m ? m[1].toLowerCase() : '';
};

/**
 * Extension for what is ACTUALLY in the container, not for what we asked for.
 *
 * `.m4a` tells Apple "MP4 audio I can play", so it is reserved for MP4 that
 * really holds AAC. MP4 carrying Opus gets the neutral `.mp4` rather than a
 * name that promises playback it cannot deliver.
 */
export function extensionFor(mime: string): string {
  const container = containerOf(mime);
  const codec = codecOf(mime);
  if (container === 'audio/mp4' || container === 'video/mp4') {
    if (container === 'video/mp4') return 'mp4';
    return codec === '' || codec.startsWith('mp4a') ? 'm4a' : 'mp4';
  }
  if (container === 'audio/ogg') return 'ogg';
  if (container === 'audio/webm') return 'webm';
  if (container === 'video/webm') return 'webm';
  // Unknown container: do not guess a playable-looking name.
  return 'bin';
}

export interface Take {
  blob: Blob;
  mime: string;
  filename: string;
  seconds: number;
  /** True when the take carries a video track as well as the master bus. */
  hasVideo: boolean;
}

export interface Recorder {
  readonly supported: boolean;
  readonly recording: boolean;
  start(): void;
  stop(): Promise<Take | null>;
}

/**
 * `videoTracks` lets a take carry the performance as well as the sound. It is
 * called at start() so the camera can be switched on and off between takes.
 */
export function createRecorder(
  ctx: AudioContext,
  tap: AudioNode,
  nameOf: () => string,
  videoTracks?: () => MediaStreamTrack[],
): Recorder {
  const supported = typeof MediaRecorder !== 'undefined' && pickMimeType() !== null;
  let rec: MediaRecorder | null = null;
  let chunks: Blob[] = [];
  let t0 = 0;
  let dest: MediaStreamAudioDestinationNode | null = null;
  let hadVideo = false;

  return {
    supported,
    get recording() { return rec !== null && rec.state === 'recording'; },
    start() {
      if (!supported || rec) return;
      dest = ctx.createMediaStreamDestination();
      tap.connect(dest);

      const video = videoTracks?.() ?? [];
      hadVideo = video.length > 0;
      const stream = new MediaStream([...dest.stream.getAudioTracks(), ...video]);
      const mime = hadVideo ? pickVideoMimeType() : pickMimeType();

      chunks = [];
      // A rejected mimeType must not lose the take: fall back to the browser default.
      try {
        rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      } catch {
        rec = new MediaRecorder(stream);
      }
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.start(250);
      t0 = ctx.currentTime;
    },
    stop() {
      return new Promise<Take | null>((resolve) => {
        // Claim the recorder before any async work: a second stop() during the
        // first's onstop wait would otherwise replace the handler — the first
        // promise then never resolves — and call MediaRecorder.stop() on an
        // inactive recorder, which throws inside the executor.
        const r = rec;
        rec = null;
        if (!r || r.state === 'inactive') return resolve(null);
        r.onstop = () => {
          const seconds = ctx.currentTime - t0;
          if (dest) { try { tap.disconnect(dest); } catch { /* already gone */ } dest = null; }
          // The recorder's own mimeType is authoritative — it carries the codec
          // that was actually negotiated, which is what names the file.
          const mime = r.mimeType || (hadVideo ? pickVideoMimeType() : pickMimeType()) || 'application/octet-stream';
          const blob = new Blob(chunks, { type: mime });
          const stamp = new Date().toISOString().slice(11, 19).replace(/:/g, '');
          resolve({
            blob, mime, seconds, hasVideo: hadVideo,
            filename: `${nameOf()}-${stamp}.${extensionFor(mime)}`,
          });
        };
        r.stop();
      });
    },
  };
}

/**
 * Video takes have exactly the same trap, and it is worth stating separately
 * because it bites again: Chromium accepts a bare 'video/mp4' and then writes
 * VP9 + Opus into it (observed: video/mp4;codecs=vp9,opus), which Apple cannot
 * decode any more than it could MP4/Opus audio.
 *
 * So the ordering is the same discipline: explicit H.264 + AAC first, then a
 * correctly labelled WebM, and bare MP4 only where WebM is impossible — Safari,
 * where bare MP4 genuinely means H.264 + AAC.
 */
const VIDEO_AVC_MP4 = [
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4;codecs=avc1.4D401E,mp4a.40.2',
  'video/mp4;codecs=h264,aac',
];
const VIDEO_WEBM = ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm'];
const VIDEO_BARE_MP4 = ['video/mp4'];

export function pickVideoMimeType(supported: SupportFn = defaultSupport): string | null {
  for (const t of VIDEO_AVC_MP4) if (supported(t)) return t;
  for (const t of VIDEO_WEBM) if (supported(t)) return t;
  for (const t of VIDEO_BARE_MP4) if (supported(t)) return t;
  return null;
}

export type Delivery = 'shared' | 'downloaded' | 'cancelled' | 'needs-gesture';

export const takeFile = (take: Take): File =>
  new File([take.blob], take.filename, { type: take.mime });

/** Can this browser share the take at all? */
export function canShareTake(take: Take): boolean {
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  return !!nav.canShare?.({ files: [takeFile(take)] });
}

/**
 * iOS path first: the share sheet is the route to Files, Photos or a DAW.
 *
 * The catch: a take does not exist until MediaRecorder's `stop` event, so by
 * the time this runs the user's tap has been spent and Safari refuses share()
 * with NotAllowedError. No rearrangement inside this function avoids that —
 * the file genuinely is not ready any earlier. So the automatic attempt is kept
 * (it works on Android Chrome inside the activation window, and `<a download>`
 * needs no activation at all on desktop), and the caller is told when a second,
 * deliberate gesture is the only way through. That distinction matters on iOS,
 * where the download fallback is a poor substitute for the share sheet.
 */
export async function deliverTake(take: Take): Promise<Delivery> {
  const shareable = canShareTake(take);
  if (shareable) {
    try {
      await navigator.share({ files: [takeFile(take)], title: take.filename });
      return 'shared';
    } catch (err) {
      // AbortError is a real decision by the user; don't nag with a button.
      if ((err as Error)?.name === 'AbortError') return 'cancelled';
      return 'needs-gesture';
    }
  }
  return downloadTake(take);
}

/** Share from a fresh user gesture — the iOS path once the take exists. */
export async function shareTakeNow(take: Take): Promise<Delivery> {
  try {
    await navigator.share({ files: [takeFile(take)], title: take.filename });
    return 'shared';
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') return 'cancelled';
    return downloadTake(take);
  }
}

export function downloadTake(take: Take): 'downloaded' {
  const url = URL.createObjectURL(take.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = take.filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return 'downloaded';
}
